import React from 'react';
import { Link } from 'react-router-dom';
import { getApi } from '../services/apiServices';
import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

function Blog() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);
  const imageLoadTimeoutRef = useRef(null);
  const [imageLoadAttempts, setImageLoadAttempts] = useState(0);
  const [componentKey, setComponentKey] = useState(Date.now()); // Force re-render key

  // Language mapping function to convert app language codes to API language codes
  const getApiLanguageCode = (appLanguage) => {
    const languageMap = {
      'ar': 'ar',     // Arabic
      'da': 'da',     // Danish
      'de': 'de',     // German
      'en': 'en',     // English
      'es': 'es',     // Spanish
      'fr': 'fr',     // French
      'he': 'he',     // Hebrew
      'it': 'it',     // Italian
      'ja': 'ja',     // Japanese
      'ko': 'ko',     // Korean
      'nl': 'nl',     // Dutch
      'no': 'no',     // Norwegian
      'pl': 'pl',     // Polish
      'pt': 'pt',     // Portuguese
      'ru': 'ru',     // Russian
      'sv': 'sv',     // Swedish
      'tr': 'tr',     // Turkish
      'zh': 'zh_CN'   // Chinese (Simplified)
    };
    return languageMap[appLanguage] || 'en'; // Default to English if not found
  };

  const fetchBlogs = async () => {
    try {
      setBlogsLoading(true);
      setImageLoading(true); // Reset image loading state when fetching blogs
      const apiLanguage = getApiLanguageCode(currentLanguage);
      const response = await getApi(`/api/blogs?lang=${apiLanguage}`);

      if (response?.status && response?.data?.blogs) {
      
        const activeBlogs = response.data.blogs.filter(blog => blog.status === 1);
        setBlogs(activeBlogs);
      } else {
        setBlogs([]);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
    } finally {
      setBlogsLoading(false);
    }
  };

  const truncateDescription = (description, maxLength = 180) => {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength).trim() + '...';
  };

  const truncateHtmlDescription = (htmlContent, maxLength = 180) => {
    if (!htmlContent) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (textContent.length <= maxLength) {
      return htmlContent;
    }
    
    const truncatedText = textContent.substring(0, maxLength).trim() + '...';
    return truncatedText;
  };

  useEffect(() => {
    fetchBlogs();
  }, [currentLanguage]);

  // Reset component key and image loading when component mounts
  useEffect(() => {
    console.log('Blog component mounted, resetting states');
    setComponentKey(Date.now());
    setImageLoading(true);
    setImageLoadAttempts(0);
    
    // Force a small delay to ensure state is properly reset
    const resetTimeout = setTimeout(() => {
      console.log('Forcing image loading reset');
      setImageLoading(false);
    }, 100);
    
    return () => clearTimeout(resetTimeout);
  }, []);

  // Reset image loading state when blogs change
  useEffect(() => {
    if (blogs.length > 0) {
      setImageLoading(true);
      setImageLoadAttempts(0); // Reset attempts counter
      
      // Clear any existing timeout
      if (imageLoadTimeoutRef.current) {
        clearTimeout(imageLoadTimeoutRef.current);
      }
      
      // Fallback timeout to prevent infinite loading
      imageLoadTimeoutRef.current = setTimeout(() => {
        console.log('Image loading timeout reached, forcing image to show');
        setImageLoading(false);
      }, 2000); // 2 second timeout
      
      return () => {
        if (imageLoadTimeoutRef.current) {
          clearTimeout(imageLoadTimeoutRef.current);
        }
      };
    }
  }, [blogs]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (imageLoadTimeoutRef.current) {
        clearTimeout(imageLoadTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <section className="what-Offer mt-0" style={{ backgroundImage: `url(${window.getPublicUrl('images/blog-bg.png')})` }}>
        <div className="container">
          <div className="blog-top-text">
            <p className="r4l-kicker">{t('r4l.pages.blogKicker')}</p>
            <h2>{t('r4l.pages.blogTitle')}</h2>
            <h3>{t('blog.subtitle')}</h3>
          </div>
        </div>
      </section>

     
      {blogs.length > 0 && (
        <section className="recent-blog">
          <div className="container">
            <h3 className="heading">{t('blog.recentPosts')}</h3>
            <div className="row">
              {blogsLoading ? (
               
                <>
                  <div className="col-sm-8">
                    <div className="blog-left">
                      <div className="blog-left-img">
                        <div style={{ height: '300px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}></div>
                      </div>
                      <div style={{ height: '20px', backgroundColor: '#f0f0f0', marginBottom: '10px', borderRadius: '4px' }}></div>
                      <div style={{ height: '16px', backgroundColor: '#f0f0f0', marginBottom: '10px', borderRadius: '4px' }}></div>
                      <div style={{ height: '60px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="blog-right">
                      {[1, 2, 3, 4, 5, 6].map((index) => (
                        <div key={index} className="blog-right-inner">
                          <div className="blog-right-inner-img">
                            <div style={{ height: '80px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}></div>
                          </div>
                          <div className="blog-right-inner-text">
                            <div style={{ height: '16px', backgroundColor: '#f0f0f0', marginBottom: '8px', borderRadius: '4px' }}></div>
                            <div style={{ height: '20px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
             
                <>
                  <div className={blogs.length === 1 ? "col-sm-12" : "col-sm-8"}>
                    <Link
                      to={`/blogDetail/${blogs[0].id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <div className="blog-left">
                        <div className="blog-left-img">
                          {imageLoading && (
                            <div style={{ 
                              height: '300px', 
                              backgroundColor: '#f0f0f0', 
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 1
                            }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                border: '3px solid #f3f3f3',
                                borderTop: '3px solid #FF7400',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}></div>
                            </div>
                          )}
                          <img
                            src={blogs[0].image_full_url || window.getPublicUrl('images/blog-left-img.jpg')}
                            alt={blogs[0].title}
                            onLoad={() => {
                              setImageLoading(false);
                              // Clear the timeout since image loaded successfully
                              if (imageLoadTimeoutRef.current) {
                                clearTimeout(imageLoadTimeoutRef.current);
                              }
                            }}
                            onError={(e) => {
                              e.target.src = window.getPublicUrl('images/blog-left-img.jpg');
                              setImageLoading(false);
                              // Clear the timeout since we handled the error
                              if (imageLoadTimeoutRef.current) {
                                clearTimeout(imageLoadTimeoutRef.current);
                              }
                            }}
                            style={{ 
                              opacity: imageLoading ? 0 : 1,
                              transition: 'opacity 0.3s ease-in-out',
                              position: 'relative',
                              zIndex: 2
                            }}
                            key={`${componentKey}-${blogs[0].id}-${blogs[0].image_full_url}`} // Force re-render when component mounts or image changes
                          />
                        </div>
                        <span><img src={window.getPublicUrl('images/blog-arrow.svg')} alt="Arrow" /></span>
                        <h2>{blogs[0].title}</h2>
                        <p dangerouslySetInnerHTML={{ __html: truncateHtmlDescription(blogs[0].description) + ` <span style="color: #FF7400; text-decoration: underline; cursor: pointer;float:none;">${t('blog.readMore')}</span>` }} />
                      </div>
                    </Link>
                  </div>
                  {blogs.length > 1 && (
                    <div className="col-sm-4">
                      <div className="blog-right">
                        {blogs.slice(1, 7).map((blog, index) => (
                          <Link
                            key={blog.id}
                            to={`/blogDetail/${blog.id}`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <div className="blog-right-inner">
                              <div className="blog-right-inner-img">
                                <img
                                  src={blog.image_full_url || window.getPublicUrl('images/blog-img1.jpg')}
                                  alt={blog.title}
                                  onError={(e) => {
                                    e.target.src = window.getPublicUrl('images/blog-img1.jpg');
                                  }}
                                />
                              </div>
                              <div className="blog-right-inner-text">
                                <span><img src={window.getPublicUrl('images/blog-arrow.svg')} alt="Arrow" /></span>
                                <h2>{blog.title}</h2>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="our-blog blog-page" id="blog-details">
        <div className="container">
          {(blogsLoading || blogs.length > 0) && (
            <h3 className="heading">{t('blog.title')}</h3>
          )}
          <div className="row">
            {blogsLoading ? (
             
              <>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <div className="col-sm-4" key={index}>
                    <div className="blog-data-show">
                      <div className="blog-img">
                        <div style={{ height: '200px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}></div>
                      </div>
                      <div style={{ height: '20px', backgroundColor: '#f0f0f0', marginBottom: '10px', borderRadius: '4px' }}></div>
                      <div style={{ height: '16px', backgroundColor: '#f0f0f0', marginBottom: '10px', borderRadius: '4px' }}></div>
                      <div style={{ height: '60px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
                    </div>
                  </div>
                ))}
              </>
            ) : blogs.length > 0 ? (
             
              blogs.map((blog, index) => (
                <div className="col-sm-4" key={blog.id}>
                  <Link
                    to={`/blogDetail/${blog.id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="blog-data-show">
                      <div className="blog-img">
                        <img
                          src={blog.image_full_url || window.getPublicUrl('images/blog-img1.jpg')}
                          alt={blog.title}
                          onError={(e) => {
                            e.target.src = window.getPublicUrl('images/blog-img1.jpg');
                          }}
                        />
                      </div>
                      <span><img src={window.getPublicUrl('images/blog-arrow.svg')} alt="Arrow" /></span>
                      <h2>{blog.title}</h2>
                      <p dangerouslySetInnerHTML={{ __html: truncateHtmlDescription(blog.description) + ` <span style="color: #FF7400; text-decoration: underline; cursor: pointer;float:none; ">${t('blog.readMore')}</span>` }} />
                    </div>
                  </Link>
                </div>
              ))
            ) : (
              
              <div className="col-12 text-center no-blog-available">
                <p>{t('blog.noBlogsAvailable')}</p>
              </div>
            )}
          </div>
        </div>
      </section>



    </>
  );
}

export default Blog;
